import { Button } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { cloneDeep } from 'lodash';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useProgress } from '@/contexts/ProgressProvider';
import { useModalContext } from '@/modals/VNModalProvider';

import { initialTaskPagination } from '../../../components/AdminContainer/ShowFeedTaskItemsModal';
import FuelActionbar from '../../../components/fuel-actionbar';
import FuelTable from '../../../components/fuel-table/FuelTable';
import type { Pagination } from '../../../models';
import type { FeedTask } from '../../../models/Ou';
import { FeedIngestService } from '../../../services/feed-ingest-service';
import useAppUtils from '../../../utils/appUtils';
import clientUtils from '../../../utils/clientUtils';
import VnModalBody from '../VnModalBody';
import VnModalFooter from '../VnModalFooter';

interface IPros {
  ouId: string;
}
const initialPagination = {
  sortBy: 'processedOn',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

const TableContent = styled.div`
  height: 480px;
`;

const ShowTaskModal = ({ ouId }: IPros) => {
  const [tasks, setTasks] = useState<FeedTask[]>([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [taskPagination, setTaskPagination] = useState(initialPagination);
  const [filteredResults, setFilteredResults] = useState<FeedTask[]>([]);
  const [feedItems, setFeedItems] = useState<any[]>([]);
  const [openSubTaskModal, setOpenSubTaskModal] = useState(false);
  const { confirm, alert } = useAppUtils();
  const columnHelper = createColumnHelper<FeedTask>();
  const { loading, setProgress } = useProgress();
  const { onClose } = useModalContext();
  const modal = useModalContext();

  const columns = [
    columnHelper.accessor('id', {
      align: 'left',
      header: () => 'Task Id',
      width: 150,
      sortable: true,
      footer: (props) => props.column.id,
    }),
    columnHelper.accessor('feedConfig.feedName', {
      header: () => <span>Feed Name</span>,
      footer: (props) => props.column.id,
      width: 150,
      sortable: false,
    }),
    columnHelper.accessor('feedConfig.feedUrl', {
      header: 'Feed Url',
      footer: (props) => props.column.id,
      sortable: false,
      width: 250,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    columnHelper.accessor('processedOn', {
      header: 'Processed On',
      footer: (props) => props.column.id,
      width: 130,
      sortable: false,
      cell: (cell) => clientUtils.formatUTCDateTime(cell.getValue()),
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      footer: (props) => props.column.id,
      width: 50,
      sortable: false,
      cell: (cell) => (
        <FuelActionbar actions={cell.row.original.actions} item={cell.row.original} onActionButtonClick={onTableAction} />
      ),
    }),
  ];
  const paginationChange = (paginationDetail: Pagination) => {
    // console.log('paginationChange', { paginationDetail });
    const paginationObj = cloneDeep(paginationDetail);
    setPagination(paginationObj);
  };
  const handleClose = () => {
    // setOpen(false);
    onClose();
  };
  //   const submit:SubmitHandler<OuForm> = async(data: OuForm) => {
  //   // console.log('Ou Submitted', data);
  //     onSave(data);
  //     // onClose();
  //   }

  const getTasks = async () => {
    try {
      setProgress(true);
      const list = await FeedIngestService.getTasksByOuId(ouId);
      const actions = [
        {
          title: 'Show',
          className: 'action',
          type: 'showtask',
          iconClass: 'icon material-icons',
          iconName: 'list',
          isDisabled: false,
        },
      ];
      if (list) {
        list.forEach((task) => {
          task.feedName = task.feedConfig.feedName;
          task.feedUrl = task.feedConfig.feedUrl;
          task.actions = actions;
        });
        setTasks(list);
        setFilteredResults(list);
        updatePagination(list.length);
      }
    } catch (err) {
    } finally {
      setProgress(false);
    }
  };

  const updatePagination = (totalItems) => {
    const paginationObj = cloneDeep(initialPagination);
    paginationObj.totalItems = totalItems;
    paginationObj.pages = Math.ceil(paginationObj.totalItems / paginationObj.rowsPerPage);
    setPagination(paginationObj);
  };

  const onTableSearch = (event, type, data) => {
    // console.log('onTableSearch', { event, type, data });
    const { searchText } = data;

    let filteredData = tasks.filter((item) => {
      return Object.values(item).join('').toLowerCase().includes(searchText.toLowerCase());
    });
    if (data.fromDate) {
      const fromDate = DateTime.fromJSDate(new Date(clientUtils.resolveDateFrom(data.fromDate, data.dateRange))).startOf('day');

      const toDate = DateTime.fromJSDate(new Date(clientUtils.resolveDateTo(data.fromDate, data.dateRange))).endOf('day');

      filteredData = filteredData.filter((a) => {
        const processedOn = DateTime.fromISO(a.processedOn);

        if (!processedOn.isValid) return false;

        return processedOn >= fromDate && processedOn <= toDate;
      });
    }
    updatePagination(filteredData.length);
    setFilteredResults(filteredData);
  };

  const onTableAction = async (event, type, feedTask: FeedTask, context: any) => {
    // console.log('onTableAction', { event, type, feedTask });
    if (type === 'showtask') {
      setProgress(true);
      const list = await FeedIngestService.getTasksByTaskId(feedTask.id);
      // console.log('getTasksByTaskId', { list });
      if (list.length === 0) {
        alert('No data found', 'warning');
      } else {
        setFeedItems(list);
        // setOpenSubTaskModal(true);

        const newPagination = _.cloneDeep(initialTaskPagination);
        newPagination.rowsPerPage = list.length;
        newPagination.totalItems = list.length;
        setTaskPagination(newPagination);
        modal.openByKey('SHOW_FEED_ITEM_TASK', { items: list, pagination: newPagination });
      }
      setProgress(false);
    }
  };

  useEffect(() => {
    if (ouId) {
      getTasks();
    }
  }, [ouId]);

  return (
    <>
      <VnModalBody>
        <TableContent>
          <FuelTable
            columnHelper={columnHelper}
            data={filteredResults}
            defaultColumns={columns}
            paginationDetail={pagination}
            paginationChange={paginationChange}
            showRowSelection={false}
            loading={loading}
            tableHeight={60}
            showActionbar
            hideActionbarButtons
            hideSearchControl={false}
            onSearch={onTableSearch}
            showDateFilter
            hidePagination
          />
        </TableContent>
      </VnModalBody>
      <VnModalFooter
        right={
          <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
            Close
          </Button>
        }
      />
    </>
  );
};

export default ShowTaskModal;
